// src/pages/Privacy.tsx
// Faithfully ports privacy.html's real content — same text, same
// table structure, same facts about hashing/encryption/cookie names.
// Only the visual styling changes to match the new design system;
// the legal/technical content itself is not rewritten.
import { Link } from 'react-router-dom';
import './Privacy.css';

export function Privacy() {
  return (
    <div className="privacy-wrap">
      <Link to="/overview" className="back-link">← Back to Dashboard</Link>

      <h1>Privacy Policy</h1>
      <p className="subtitle">Idle Clans Hub — Bot Dashboard · Last updated 30 June 2026</p>

      <p>
        This page explains what data the bot dashboard collects when you use it, why,
        how long it is kept, and how to have it removed. This policy covers the{' '}
        <strong>botdash.idlehub.tech</strong> dashboard. For the public website
        (<strong>idlehub.tech</strong>), see its separate{' '}
        <a href="https://idlehub.tech/privacy.html">privacy policy</a>.
      </p>

      <h2>What we collect</h2>
      <table>
        <thead>
          <tr><th>Data</th><th>Why</th><th>Kept for</th></tr>
        </thead>
        <tbody>
          <tr>
            <td>Discord user ID, username, avatar</td>
            <td>Identify your account and which servers you can manage</td>
            <td><span className="badge badge-session">Session only — cleared on logout</span></td>
          </tr>
          <tr>
            <td>Discord server (guild) ID and name</td>
            <td>Apply your bot settings to the correct server</td>
            <td><span className="badge badge-permanent">Until you remove the bot from your server</span></td>
          </tr>
          <tr>
            <td>Server settings, role assignments, command overrides, market/clan watch lists, reaction roles, aliases, welcome/leave messages</td>
            <td>Core bot configuration — your choices, stored so they persist between bot restarts</td>
            <td><span className="badge badge-permanent">Until you delete them or remove the bot</span></td>
          </tr>
          <tr>
            <td>Command usage counts (which command, success/failure, response time)</td>
            <td>Show usage statistics in the dashboard and on the public website</td>
            <td><span className="badge badge-disabled">Currently kept indefinitely — auto-pruning is built but disabled</span></td>
          </tr>
          <tr>
            <td>Discord user ID linked to a command use</td>
            <td>Allows the bot owner to investigate abuse reports if needed</td>
            <td>Stored <strong>encrypted</strong> (AES-256), not in plain text — see below</td>
          </tr>
          <tr>
            <td>Error log entries (hashed IP, request path, error message)</td>
            <td>Diagnose bugs</td>
            <td><span className="badge badge-7">7 days, then auto-deleted</span></td>
          </tr>
          <tr>
            <td>Rate limit hit records (hashed IP, command, timestamp)</td>
            <td>Detect abuse patterns and spam</td>
            <td>Kept indefinitely for trend analysis — no personal data beyond a hashed IP</td>
          </tr>
          <tr>
            <td>Bug reports you submit (description, category, optional screenshot link)</td>
            <td>Let us fix problems you report</td>
            <td>Kept until resolved and manually cleared by the bot owner</td>
          </tr>
        </tbody>
      </table>

      <h2>What we do NOT collect</h2>
      <ul>
        <li>We do not store your Discord access or refresh tokens — they are discarded immediately after login.</li>
        <li>We do not collect email addresses, phone numbers, or payment information.</li>
        <li>We do not use advertising or third-party tracking cookies.</li>
        <li>We do not sell or share your data with third parties.</li>
        <li>We do not read your Discord messages or DMs — the bot only responds to slash commands.</li>
      </ul>

      <h2>Encrypted vs hashed data — what&apos;s the difference?</h2>
      <p>
        Some data is <strong>hashed</strong> (IP addresses, bug report reporter identifiers) — this is
        one-way and cannot be reversed by anyone, including us. Other data is <strong>encrypted</strong>{' '}
        (Discord user IDs linked to command usage) — this can be decrypted only by the bot owner using a
        private key, solely to investigate abuse or technical issues. It is never decrypted for routine
        analytics, which only use aggregate counts.
      </p>

      <h2>Cookies</h2>
      <p>
        This dashboard uses two essential cookies, both required for the site to function and exempt
        from cookie consent requirements as &quot;strictly necessary&quot; cookies:
      </p>
      <ul>
        <li><code>connect.sid</code> — keeps you logged in. Contains only an opaque session ID, no personal data. <code>HttpOnly</code>, <code>Secure</code>, <code>SameSite=Lax</code>. Expires after 7 days of inactivity.</li>
        <li><code>__Host-psifi.x-csrf-token</code> — protects against cross-site request forgery attacks on actions like saving settings. Contains a random token, no personal data. <code>HttpOnly</code>, <code>Secure</code>.</li>
      </ul>
      <p>We do not use any analytics, advertising, or third-party tracking cookies.</p>

      <h2>Your rights</h2>
      <p>
        You can delete your server&apos;s stored settings, roles, watch lists, reaction roles, and aliases
        at any time from the dashboard, or by removing the bot from your server. To request deletion of
        any other data linked to your Discord account (such as bug reports you submitted), contact the
        bot owner via the support server linked on the website.
      </p>
      <p>
        If you are in the European Economic Area, you have rights under GDPR including access,
        correction, deletion, and data portability for any personal data we hold about you.
      </p>

      <h2>Third-party services</h2>
      <p>
        Login is handled by{' '}
        <a href="https://discord.com/privacy" target="_blank" rel="noopener noreferrer">Discord OAuth 2.0</a>.
        We only request the <code>identify</code> and <code>guilds</code> scopes — the minimum needed to
        identify you and list servers you own. We do not access your friends list, messages, or DMs.
        Player and clan data displayed by the bot and website comes from the public{' '}
        <a href="https://query.idleclans.com" target="_blank" rel="noopener noreferrer">Idle Clans API</a>,
        which is not affiliated with this dashboard.
      </p>

      <h2>Changes to this policy</h2>
      <p>
        If we change what data we collect or how we use it, we will update the date at the top of this
        page and post a notice in the bot&apos;s support server.
      </p>
    </div>
  );
}
